import React, { useEffect, useState } from "react";
import { X, Check, ArrowRight, ShieldCheck, HelpCircle, Briefcase, Sparkles } from "lucide-react";
import { Matter } from "../../lib/mockState";

interface PromptLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  matters: Matter[];
  onLaunchAudit: (launcherData: {
    templateId: string;
    templateName: string;
    matterId: string;
    executor: string;
    scopes: string[];
    customCommand: string;
  }) => void;
  isAr?: boolean;
}

interface TemplateItem {
  id: string;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  executor: string;
  defaultPrompt: string;
  defaultScopes: string[];
}

export const PromptLibrary: React.FC<PromptLibraryProps> = ({
  isOpen,
  onClose,
  matters = [],
  onLaunchAudit,
  isAr = false,
}) => {
  const [activeTab, setActiveTab] = useState<"matter" | "portfolio" | "owner">("matter");
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateItem | null>(null);
  const [selectedMatterId, setSelectedMatterId] = useState("");
  const [checkedScopes, setCheckedScopes] = useState<string[]>([]);
  const fallbackMatterId = matters[0]?.matterId || "CASE-2026-0042";

  // Templates definitions
  const templates: Record<"matter" | "portfolio" | "owner", TemplateItem[]> = {
    matter: [
      {
        id: "difc-timeline",
        name: "DIFC Fact & Chronology Auditor",
        nameAr: "مدقق التسلسل الزمني وبينات مركز دبي المالي",
        description: "Scans docket entries & attachments to flag timeline gaps and non-cryptosealed local exhibits.",
        descriptionAr: "يبحث في مستندات القضية لتكشيف فجوات التواريخ والبينات غير الموثقة في محاكم مركز دبي المالي.",
        executor: "Alex",
        defaultPrompt: "Conduct a DIFC timeline fact-audit on this selected case matter. Cross-check all PDF exhibits for inconsistent logging and verify if any evidence lacks an active digital authenticity seal.",
        defaultScopes: ["Inconsistent Loggings", "Cryptographic Asset Seal", "Exclusion Verify"],
      },
      {
        id: "court-pleading-trans",
        name: "Bilingual Certified UAE Translator",
        nameAr: "مترجم المذكرات المعتمد ثنائي اللغة",
        description: "Translates adversarial complaints to standardized legal force Arabic for local Dubai judicial filing.",
        descriptionAr: "يترجم المذكرات والشكاوى الإنجليزية لمصطلحات عربية معتمدة ومتطابقة مع الصياغة القضائية المحلية.",
        executor: "Tareq",
        defaultPrompt: "Perform certified Ar-En translation checks on the main statement of claim, aligning terminology with UAE Federal Court standards.",
        defaultScopes: ["Bilinguality Conformity", "certified UAE terminology"],
      },
      {
        id: "witness-credibility",
        name: "Testimony Contradiction Scanner",
        nameAr: "كاشف تناقضات شهادات الشهود",
        description: "Audits deposition transcripts against expert statements to flag cross-examination opportunities.",
        descriptionAr: "يقارن شهادات الشهود لإيجاد التناقضات وصياغة أسئلة للمرافعة القضائية.",
        executor: "Alex",
        defaultPrompt: "Verify expert witness credibility coefficients by matching the constructor specifications reports with deposition item #3 testimony statements.",
        defaultScopes: ["Witness Conflict Flags", "Cross-Exam Questions Generation"],
      },
    ],
    portfolio: [
      {
        id: "lease-notice-window",
        name: "Termination Notice Window Monitor",
        nameAr: "مراقب نوافذ إنهاء العقود والالتزامات",
        description: "Scans active leases to calculate strict landlord notice windows and prevent automated roll-overs.",
        descriptionAr: "يفحص عقود الإيجار لحساب تواريخ الإخطارات الإجبارية لتفادي التجديد التلقائي غير المبرر.",
        executor: "Sofia",
        defaultPrompt: "Scans lease agreement portfolios for the termination opt-out clause, calculating precise milestone obligation alerts.",
        defaultScopes: ["Post-Execution Obligations", "Timeline Alerts Schedule"],
      },
      {
        id: "indemnity-liability",
        name: "Indemnity & Liability Cap Auditor",
        nameAr: "مدقق حدود المسؤولية والتعويضات",
        description: "Cross-checks limitations of liability clauses directly against the corporate risk Golden Rules.",
        descriptionAr: "يدير مخاطر الالتزام عن طريق تدقيق مستويات التعويض في العقود ومقارنتها بقواعد حوكمة الشركة.",
        executor: "Sofia",
        defaultPrompt: "Analyze the limitation of liability section. Assess drift from standard corporate caps and advise on negotiation positioning.",
        defaultScopes: ["Golden Rule Deviation Caps", "Negotiation positioning advice"],
      },
      {
        id: "sharia-banking",
        name: "Sharia Legal Conformity Scan",
        nameAr: "فاحص مطابقة أحكام المعاملات المالية الإسلامية",
        description: "Scans investment agreements for conformity under standard Islamic Finance Sharia guidelines.",
        descriptionAr: "يفحص الأوراق التعاقدية للتثبت من مطابقتها لقواعد التمويل الإسلامي والمعايير المحلية.",
        executor: "Rashid",
        defaultPrompt: "Scan all clauses in the financing deck to ensure strict conformity with Sharia compliance thresholds and DIFC Civil regulations.",
        defaultScopes: ["Riba/Interest Flags", "Sharia compliance parameters review"],
      },
    ],
    owner: [
      {
        id: "morning-briefing",
        name: "Morning Briefing",
        nameAr: "الإحاطة الصباحية",
        description: "Builds a morning briefing across active deadlines, alerts, and KYC exposure for the selected matter portfolio.",
        descriptionAr: "يبني إحاطة صباحية عبر المواعيد والتنبيهات وحالة KYC للملف المحدد.",
        executor: "System",
        defaultPrompt: "Generate a morning briefing for this matter portfolio. Include active deadlines, unresolved alerts, KYC pressure points, and the next best action.",
        defaultScopes: ["Deadlines", "Alerts", "KYC posture"],
      },
      {
        id: "dunning-sweep",
        name: "Automated Outstanding Dunning Review",
        nameAr: "مصحح ومراجع مطالبات التحصيل المتأخرة",
        description: "Sweeps accounts receivable ledgers to prioritize client notifications and prepare dunning briefs.",
        descriptionAr: "تمشيط سجل المقبوضات المعلقة لجدولة الإرسال التلقائي لمطالبات السداد القانونية للعملاء المخالفين.",
        executor: "System",
        defaultPrompt: "Triggers accounts outstanding analysis sweep. Draft statutory warning letters for warning clients list.",
        defaultScopes: ["Dunning warning drafts", "AR outstanding prioritizing"],
      },
      {
        id: "billing-realization",
        name: "WIP Billing Integrity Audit",
        nameAr: "مدقق سلامة ومستحقات الساعات غير المفوترة",
        description: "Compares active lawyer work dockets against client retainer rules to spot billable leakages.",
        descriptionAr: "يقارن مستحقات الساعات المعلقة (WIP) مع البنود المنجزة لتجنب ضياع الاتعاب.",
        executor: "System",
        defaultPrompt: "Generate billing leakage assessment score comparing WIP indicators against logged matter tasks.",
        defaultScopes: ["Leakage flag identifier", "Attribution checks"],
      },
    ],
  };

  const handleSelectTemplate = (tpl: TemplateItem) => {
    setSelectedTemplate(tpl);
    setCheckedScopes([...tpl.defaultScopes]);
    // Pre-select first appropriate matter of matching species if available
    const matchingMatter = activeTab === "owner"
      ? matters[0]
      : matters.find((matter) => matter.matterSpecies === (activeTab === "portfolio" ? "CONTRACT" : "CASE"));
    if (matchingMatter) {
      setSelectedMatterId(matchingMatter.matterId);
    } else if (matters.length > 0) {
      setSelectedMatterId(matters[0].matterId);
    } else if (activeTab === "owner") {
      setSelectedMatterId(fallbackMatterId);
    } else {
      setSelectedMatterId("");
    }
  };

  const handleToggleScope = (scope: string) => {
    if (checkedScopes.includes(scope)) {
      setCheckedScopes(prev => prev.filter(s => s !== scope));
    } else {
      setCheckedScopes(prev => [...prev, scope]);
    }
  };

  const handleLaunch = () => {
    if (!selectedTemplate || !selectedMatterId) return;

    onLaunchAudit({
      templateId: selectedTemplate.id,
      templateName: selectedTemplate.name,
      matterId: selectedMatterId,
      executor: selectedTemplate.executor,
      scopes: checkedScopes,
      customCommand: selectedTemplate.defaultPrompt,
    });

    // Reset and close
    setSelectedTemplate(null);
    onClose();
  };

  // Filter matters based on current template tab
  const getFilteredMatters = () => {
    if (activeTab === "owner") {
      return matters;
    }
    if (activeTab === "portfolio") {
      return matters.filter(m => m.matterSpecies === "CONTRACT");
    }
    return matters.filter(m => m.matterSpecies === "CASE");
  };

  useEffect(() => {
    if (!selectedTemplate || selectedMatterId) return;
    const filteredMatters = getFilteredMatters();
    if (filteredMatters[0]) {
      setSelectedMatterId(filteredMatters[0].matterId);
    } else if (activeTab === "owner") {
      setSelectedMatterId(fallbackMatterId);
    }
  }, [activeTab, fallbackMatterId, matters, selectedMatterId, selectedTemplate]);

  const currentTabTemplates = templates[activeTab];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm select-none" dir={isAr ? "rtl" : "ltr"}>
      
      {/* Modal Container Card */}
      <div className="w-full max-w-4xl bg-white rounded-3xl border border-slate-205 shadow-2xl flex flex-col max-h-[85vh] overflow-hidden animate-scale-in">
        
        {/* Header strip */}
        <div className={`p-6 border-b border-slate-100 flex items-center justify-between bg-white ${isAr ? "flex-row-reverse" : "flex-row"}`}>
          <div className={`flex items-center gap-3 ${isAr ? "flex-row-reverse" : "flex-row"}`}>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 border border-orange-100/50 text-orange-600">
              <Sparkles size={20} strokeWidth={2} />
            </div>
            <div className={`${isAr ? "text-right" : ""}`}>
              <h3 className="font-display font-medium text-slate-900 text-lg tracking-tight">
                {isAr ? "مكتبة نماذج الذكاء المستقل" : "Prompt Intelligence Library"}
              </h3>
              <p className="text-xs text-slate-500 font-medium leading-none mt-1">
                {isAr ? "شغل تدقيقاً قانونياً مجمعاً بنقرات سريعة" : "Launch certified judicial and contract auditing engines on active portfolios"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-900 transition-colors cursor-pointer"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Modal Content Split: Categories Left list, Form details Right */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row min-h-0">
          
          {/* Left panel: Category Tabs and template picker (Takes 5/12) */}
          <div className="w-full md:w-5/12 border-b md:border-b-0 md:border-r border-slate-100 flex flex-col p-5 bg-slate-50/50 overflow-y-auto premium-short-scrollbar">
            
            {/* Category tabs */}
            <div className="grid grid-cols-3 gap-2 bg-slate-100/60 p-1.5 rounded-2xl mb-6 text-xs font-semibold shadow-inner">
              <button
                onClick={() => { setActiveTab("matter"); setSelectedTemplate(null); }}
                className={`py-2 px-3 rounded-xl text-center cursor-pointer transition-all ${
                  activeTab === "matter" ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-900/5" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {isAr ? "قضايا" : "Matter"}
              </button>
              <button
                onClick={() => { setActiveTab("portfolio"); setSelectedTemplate(null); }}
                className={`py-2 px-3 rounded-xl text-center cursor-pointer transition-all ${
                  activeTab === "portfolio" ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-900/5" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {isAr ? "عقود" : "Portfolio"}
              </button>
              <button
                onClick={() => { setActiveTab("owner"); setSelectedTemplate(null); }}
                className={`py-2 px-3 rounded-xl text-center cursor-pointer transition-all ${
                  activeTab === "owner" ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-900/5" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {isAr ? "إدارة" : "Firm"}
              </button>
            </div>

            {/* Templates list layout */}
            <div className="space-y-3">
              <span className={`text-[10px] font-mono font-bold text-slate-400 block uppercase tracking-wider pl-1 mb-1 ${isAr ? "text-right" : ""}`}>
                {isAr ? "القوالب المتوفرة:" : "AVAILABLE RECIPES:"}
              </span>
              {currentTabTemplates.map((tpl) => {
                const isChosen = selectedTemplate?.id === tpl.id;
                return (
                  <div
                    key={tpl.id}
                    data-testid={`prompt-library-template-${tpl.id}`}
                    onClick={() => handleSelectTemplate(tpl)}
                    className={`group p-4 rounded-2xl border cursor-pointer transition-all duration-200 ${
                      isChosen
                        ? "bg-slate-900 border-slate-900 shadow-md ring-4 ring-slate-900/10 scale-[1.02]"
                        : "bg-white hover:bg-slate-50 border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md text-slate-800"
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2 mb-1.5">
                      <h4 className={`font-semibold text-sm leading-snug line-clamp-2 ${isChosen ? "text-white" : "text-slate-900"}`}>
                        {isAr ? tpl.nameAr : tpl.name}
                      </h4>
                      <span className={`text-[9px] font-mono px-2 py-1 rounded-md uppercase font-semibold shrink-0 ${
                        isChosen ? "bg-orange-500/20 text-orange-200" : "bg-slate-100 text-slate-500 group-hover:bg-slate-200"
                      }`}>
                        {tpl.executor}
                      </span>
                    </div>
                    <p className={`text-xs leading-relaxed font-medium line-clamp-2 ${
                      isChosen ? "text-slate-300" : "text-slate-500"
                    }`}>
                      {isAr ? tpl.descriptionAr : tpl.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right panel: Template Interactive Configuration Form (Takes 7/12) */}
          <div className="flex-1 p-5 flex flex-col overflow-y-auto bg-white justify-between premium-short-scrollbar">
            {!selectedTemplate ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-5 p-8 opacity-80 animate-fade-in">
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl shadow-sm text-slate-400">
                  <Briefcase size={32} strokeWidth={1} />
                </div>
                <div className="space-y-2 max-w-sm mx-auto">
                  <h4 className="font-display font-medium text-slate-900 text-lg tracking-tight">
                    {isAr ? "مكوّن المهام والقوالب التفاعلية" : "Recipe Configurer"}
                  </h4>
                  <p className="text-xs text-slate-500 font-medium leading-relaxed max-w-[250px] mx-auto">
                    {isAr ? "قم باختيار قالب جاهز من القائمة الجانبية لإدارة البنود ونطاقات الفحص للبدء" : "Pick a blueprint from the registry to configure target matters and set custom scopes."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                
                {/* Active template summary header */}
                <div className={`p-5 rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100/30 border border-orange-100 space-y-2 relative overflow-hidden ${isAr ? "text-right" : ""}`}>
                  <div className="absolute right-0 top-0 w-24 h-24 bg-gradient-to-br from-orange-200/40 to-transparent blur-2xl rounded-full" />
                  <div className={`flex items-center gap-1.5 text-[10px] font-mono font-bold text-orange-600 uppercase tracking-widest relative z-10 ${isAr ? "flex-row-reverse" : ""}`}>
                    <Sparkles size={12} className="animate-pulse" />
                    <span>{isAr ? "المُعامل النشط لعملية التدقيق الداخلي" : "ACTIVE CONFIGURATION SCOPE"}</span>
                  </div>
                  <h4 className="font-display font-medium text-slate-900 text-lg relative z-10">
                    {isAr ? selectedTemplate.nameAr : selectedTemplate.name}
                  </h4>
                  <p className="text-xs font-medium text-slate-600 leading-relaxed relative z-10">
                    {isAr ? selectedTemplate.descriptionAr : selectedTemplate.description}
                  </p>
                </div>

                {/* Form Field 1: Entity Picker Dropdown */}
                <div className="space-y-2 pt-2">
                  <label className={`text-[10px] font-mono font-semibold text-slate-500 block uppercase tracking-wider ${isAr ? "text-right" : ""}`}>
                    {isAr ? "١. حدد ملف القضية أو العقد المُستهدف:" : "1. SELECT CASE / CONTRACT MATTER MODULE:"}
                  </label>
                  <select
                    value={selectedMatterId}
                    onChange={(e) => setSelectedMatterId(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none text-sm font-medium text-slate-800 cursor-pointer focus:ring-1 focus:ring-slate-900 focus:border-slate-900 shadow-sm transition-all"
                  >
                    <option value="" disabled>{isAr ? "اختر من السجلات..." : "Please choose an active matter..."}</option>
                    {getFilteredMatters().length === 0 && activeTab === "owner" && (
                      <option value={fallbackMatterId}>{fallbackMatterId} — Firm portfolio briefing</option>
                    )}
                    {getFilteredMatters().map((m) => (
                      <option key={m.matterId} value={m.matterId}>
                        {m.matterId} — {m.clientName} ({m.title})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Form Field 2: Scopes Checkboxes */}
                <div className="space-y-2 pt-2">
                  <label className={`text-[10px] font-mono font-semibold text-slate-500 block uppercase tracking-wider ${isAr ? "text-right" : ""}`}>
                    {isAr ? "٢. حدد نطاق التدقيق وعناصر الفحص:" : "2. CONFIGURE REGULATORY AUDIT SCRUTINY SCOPE:"}
                  </label>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedTemplate.defaultScopes.map((scope) => {
                      const isChecked = checkedScopes.includes(scope);
                      return (
                        <div
                          key={scope}
                          onClick={() => handleToggleScope(scope)}
                          className={`p-3 rounded-xl border cursor-pointer transition-all duration-200 flex items-center justify-between group ${
                            isChecked
                              ? "bg-slate-50 border-slate-900 ring-1 ring-slate-900 shadow-sm"
                              : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-xs"
                          }`}
                        >
                          <span className={`text-xs font-semibold truncate transition-colors ${isChecked ? "text-slate-900" : "text-slate-600 group-hover:text-slate-900"}`}>{scope}</span>
                          <div className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 transition-all duration-200 ${
                            isChecked ? "bg-slate-900 border-slate-900 text-white" : "bg-slate-50 border-slate-200 group-hover:border-slate-300"
                          }`}>
                            {isChecked && <Check size={12} strokeWidth={3} />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Form Field 3: Preview prompt command */}
                <div className="space-y-2 pt-2 flex-1 flex flex-col">
                  <label className={`text-[10px] font-mono font-semibold text-slate-500 block uppercase tracking-wider ${isAr ? "text-right" : ""}`}>
                    {isAr ? "أمر التشغيل المجدول (معاينة):" : "SIMULATION INSTRUCTION SET:"}
                  </label>
                  <div className={`flex-1 p-4 rounded-xl bg-slate-50/50 border border-slate-200/80 text-xs text-slate-600 font-medium leading-relaxed overflow-y-auto ${isAr ? "text-right" : ""}`}>
                    {selectedTemplate.defaultPrompt}
                  </div>
                </div>

              </div>
            )}

            {/* Launch CTA actions bar */}
            {selectedTemplate && (
              <div className={`pt-5 border-t border-slate-100 flex items-center justify-between mt-4 ${isAr ? "flex-row-reverse" : "flex-row"}`}>
                <div className={`flex items-center gap-2 text-[10px] font-mono font-semibold text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200/60 ${isAr ? "flex-row-reverse" : "flex-row"}`}>
                  <Briefcase size={12} className="text-slate-400" />
                  <span>{isAr ? `العميل المجدول: ` : "EXECUTOR CACHE: "}<strong className="text-slate-700">{selectedTemplate.executor}</strong></span>
                </div>
                <div className={`flex items-center gap-3 ${isAr ? "flex-row-reverse" : ""}`}>
                  <button
                    onClick={() => setSelectedTemplate(null)}
                    className="px-4 py-2 hover:bg-slate-100 text-slate-600 hover:text-slate-900 font-semibold text-sm rounded-xl cursor-pointer transition-all"
                  >
                    {isAr ? "إلغاء القالب" : "Back"}
                  </button>
                  <button
                    data-testid="prompt-library-run-audit"
                    onClick={handleLaunch}
                    disabled={!selectedMatterId || checkedScopes.length === 0}
                    className="px-6 py-2.5 bg-slate-900 hover:bg-black disabled:bg-slate-100 disabled:text-slate-400 text-white font-semibold text-sm rounded-xl cursor-pointer transition-all flex items-center gap-2 shadow-sm shadow-slate-900/10 active:scale-[0.98]"
                  >
                    <span>{isAr ? "تشغيل التدقيق مستقل" : "Run Audit"}</span>
                    <ArrowRight size={14} className={isAr ? "rotate-180" : ""} />
                  </button>
                </div>
              </div>
            )}

          </div>

        </div>

      </div>

    </div>
  );
};
